// src/components/Atelier3/Activite1/StakeholderSummaryTable.js
import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const getThreatZone = (threatLevel) => {
  if (threatLevel >= 3) return "danger";
  if (threatLevel >= 1.5) return "control";
  if (threatLevel >= 0.5) return "watch";
  return "outside";
};

const getThreatZoneStyles = (zone) => {
  switch (zone) {
    case "danger": return "text-red-700 bg-red-100 border border-red-300";
    case "control": return "text-orange-700 bg-orange-100 border border-orange-300";
    case "watch": return "text-teal-700 bg-teal-100 border border-teal-300";
    default: return "text-slate-700 bg-slate-100 border border-slate-300";
  }
};

const StakeholderSummaryTable = ({ stakeholders, categoryNamesById = {} }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("all");
  const [sortField, setSortField] = useState("threatLevel");
  const [sortDirection, setSortDirection] = useState("desc");

  const getZoneText = (zone) => {
    switch (zone) {
      case "danger": return t("workshop3.activity1.legend.danger", "Danger");
      case "control": return t("workshop3.activity1.legend.control", "Contrôle");
      case "watch": return t("workshop3.activity1.legend.watch", "Veille");
      default: return t("workshop3.activity1.summary.outside", "Hors périmètre");
    }
  };

  const getCategoryText = (category) => {
    if (categoryNamesById[category]) return categoryNamesById[category];
    switch (category) {
      case "client": return t("workshop3.activity1.categories.client", "Client");
      case "partner": return t("workshop3.activity1.categories.partner", "Partenaire");
      case "provider": return t("workshop3.activity1.categories.provider", "Prestataire");
      case "technical": return t("workshop3.activity1.categories.technical", "Service technique");
      case "business": return t("workshop3.activity1.categories.business", "Service métier");
      case "subsidiary": return t("workshop3.activity1.categories.subsidiary", "Filiale");
      default: return category;
    }
  };

  const getTypeText = (type) =>
    type === "internal"
      ? t("workshop3.activity1.filter.internal", "Interne")
      : t("workshop3.activity1.filter.external", "Externe");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedRows = useMemo(() => {
    const filtered = stakeholders.filter((s) => {
      if (filter === "critical") return s.critical;
      if (filter === "retained") return s.retained;
      return true;
    });

    return [...filtered].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [stakeholders, filter, sortField, sortDirection]);

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc"
      ? <ChevronUp size={14} className="ml-1" />
      : <ChevronDown size={14} className="ml-1" />;
  };

  const filterButtons = [
    { key: "all", label: t("workshop3.activity1.summary.filters.all", "Toutes") },
    { key: "critical", label: t("workshop3.activity1.summary.filters.critical", "Critiques") },
    { key: "retained", label: t("workshop3.activity1.summary.filters.retained", "Retenues") },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-medium text-gray-700 flex items-center">
            <AlertTriangle size={18} className="mr-2 text-red-600" />
            {t("workshop3.activity1.summary.title", "Synthèse des parties prenantes")}
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            {t("workshop3.activity1.summary.subtitle", "Vue d'ensemble avec mise en évidence des parties prenantes critiques")}
          </p>
        </div>
        <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-lg">
          {filterButtons.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                filter === key
                  ? "bg-white text-blue-700 shadow-sm border border-slate-200"
                  : "text-slate-600 hover:bg-white/60"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {sortedRows.length === 0 ? (
        <div className="p-8 text-center">
          <Users size={32} className="text-blue-500 mx-auto mb-2" />
          <p className="text-gray-600">
            {t("workshop3.activity1.summary.empty", "Aucune partie prenante à afficher")}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="text-xs uppercase text-slate-500 bg-slate-50 border-b border-slate-200">
                <th className="px-3 py-3 font-medium w-[45px]">
  {t("workshop3.activity1.summary.columns.number", "#")}
</th>
                <th className="px-3 py-3 font-medium cursor-pointer select-none" onClick={() => handleSort("name")}>
                  {t("workshop3.activity1.summary.columns.name", "Nom")}{renderSortIcon("name")}
                </th>
                <th className="px-3 py-3 font-medium cursor-pointer select-none" onClick={() => handleSort("category")}>
                  {t("workshop3.activity1.summary.columns.category", "Catégorie")}{renderSortIcon("category")}
                </th>
                <th className="px-3 py-3 font-medium">
                  {t("workshop3.activity1.summary.columns.typeRank", "Type / Rang")}
                </th>
                <th className="px-3 py-3 font-medium cursor-pointer select-none" onClick={() => handleSort("threatLevel")}>
                  {t("workshop3.activity1.summary.columns.threat", "Menace")}{renderSortIcon("threatLevel")}
                </th>
                <th className="px-3 py-3 font-medium">
                  {t("workshop3.activity1.summary.columns.zone", "Zone")}
                </th>
                <th className="px-3 py-3 font-medium">
                  {t("workshop3.activity1.summary.columns.retained", "Retenue")}
                </th>
                <th className="px-3 py-3 font-medium">
                  {t("workshop3.activity1.summary.columns.critical", "Critique")}
                </th>
                <th className="px-3 py-3 font-medium min-w-[200px]">
                  {t("workshop3.activity1.summary.columns.justification", "Justification")}
                </th>
                <th className="px-3 py-3 font-medium min-w-[200px]">
                  {t("workshop3.activity1.summary.columns.description", "Description")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((s, index) => {
                const zone = getThreatZone(s.threatLevel);
                const zoneStyles = getThreatZoneStyles(zone);
                const justification = s.criticalJustification || "";
                return (
                  <tr
                    key={s.id}
                    className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                      s.critical ? "border-l-4 border-l-red-500 bg-red-50/60" : ""
                    }`}
                  >
                    <td className="px-3 py-2 text-xs text-slate-400">{index + 1}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{s.name}</td>
                    <td className="px-3 py-2 text-slate-600">{getCategoryText(s.category)}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {getTypeText(s.type)} ({s.rank})
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        s.threatLevel >= 3 ? "text-red-700 bg-red-100" :
                        s.threatLevel >= 1.5 ? "text-orange-700 bg-orange-100" :
                        "text-teal-700 bg-teal-100"
                      }`}>
                        {Number(s.threatLevel || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${zoneStyles}`}>
                        {getZoneText(zone)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        s.retained ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"
                      }`}>
                        {s.retained
                          ? t("workshop3.activity1.summary.yes", "Oui")
                          : t("workshop3.activity1.summary.no", "Non")}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {s.critical ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                          <AlertTriangle size={12} className="mr-1" />
                          {t("workshop3.activity1.summary.criticalBadge", "Critique")}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500 max-w-[280px]">
                      <span
                        className="block truncate"
                        title={justification}
                      >
                        {justification || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500 max-w-[280px]">
                      <span className="block whitespace-normal">
                        {s.description || "—"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StakeholderSummaryTable;